// @ts-check
/**
 * src/utils/userDisplay.js
 *
 * The one place an internal actor id becomes something a human may see.
 *
 * Internal ids (`user-q51ge1m4s`, employee ids) are Tryggr's own plumbing —
 * they must never surface in the UI, in an export, or in an email. Every
 * id → name resolution goes through here so that a fallback can never quietly
 * print the id itself: an id that resolves to nobody renders as
 * UNKNOWN_USER_LABEL, not as the id.
 *
 * Two collections can carry a name for the same person: `state.users` (tenant
 * logins) and `state.employees` (HR records, linked to a login via
 * `employee.linkedUserId`). An owner id may point at either, so both are
 * searched — users first, since a login is the more specific record.
 */

/** Rendered when an id is present but matches no known person. */
import { isSampleEmployee } from './sampleRecords.js';
export const UNKNOWN_USER_LABEL = 'Unknown user';

/**
 * A tenant login record (`state.users[]`) or an HR record
 * (`state.employees[]`) — both are matched structurally, not by type.
 * @typedef {Record<string, any>} PersonRecord
 */

/**
 * Best human-readable name carried by a single tenant user record.
 * Returns '' when the record carries no name at all — never the id.
 *
 * @param {PersonRecord | null | undefined} user
 * @returns {string}
 */
export function userDisplayName(user) {
  if (!user) return '';
  return (
    user.displayName ||
    user.fullName ||
    [user.firstName, user.lastName].filter(Boolean).join(' ') ||
    user.name ||
    user.username ||
    user.email ||
    ''
  );
}

/**
 * Best human-readable name carried by a single employee record.
 * Returns '' when the record carries no name at all — never the id.
 *
 * @param {PersonRecord | null | undefined} employee
 * @returns {string}
 */
export function employeeDisplayName(employee) {
  if (!employee) return '';
  return (
    employee.name ||
    employee.fullName ||
    employee.displayName ||
    [employee.firstName, employee.lastName].filter(Boolean).join(' ') ||
    employee.email ||
    ''
  );
}

/**
 * Resolve an owner / assignee / reviewer id to a display name.
 *
 * @param {string | null | undefined} id
 * @param {{ users?: PersonRecord[] | null, employees?: PersonRecord[] | null }} [people]
 * @param {{ empty?: string, unknown?: string }} [opts]
 *   `empty`   — returned when no id was supplied (default '—'; derivation
 *               layers that feed Workbench filters pass '').
 *   `unknown` — returned when the id matches nobody (default 'Unknown user').
 * @returns {string} never the raw id
 */
export function resolveDisplayName(id, people = {}, opts = {}) {
  const { empty = '—', unknown = UNKNOWN_USER_LABEL } = opts;
  if (!id) return empty;

  const users = people.users || [];
  const employees = people.employees || [];

  const user = users.find((u) => u && (u.id === id || u.userId === id));
  const fromUser = userDisplayName(user);
  if (fromUser) return fromUser;

  // Direct employee id, then an employee linked to this login.
  const employee =
    employees.find((e) => e && e.id === id) || employees.find((e) => e && e.linkedUserId === id);
  const fromEmployee = employeeDisplayName(employee);
  // Every surface that names a sample person says so (GH #7870), so a risk
  // "owned by" a sample reads the same as the owner resolution that refuses it.
  if (fromEmployee) return isSampleEmployee(employee) ? `${fromEmployee} (sample)` : fromEmployee;

  return unknown;
}
